import { findSportConfig } from "../../../../config/sportsConfig";
import { getParticipantName } from "./matchDisplayUtils";

/**
 * Extract scoring rules for a match, in priority order:
 *  1. match.matchConfig.scoring_rules  (saved per-match settings)
 *  2. SPORTS_CONFIG defaults by sport name
 *  3. Generic fallback (no limits)
 */
export const getMatchScoringRules = (match) => {
  const matchConfig = match?.matchConfig || match?.match_config || {};
  const sr = matchConfig.scoring_rules || {};

  // Per-match saved values (may be undefined/null)
  const savedPtw   = sr.points_to_win;
  const savedMargin = sr.win_by_margin;
  const savedMax   = sr.max_points;

  // sportsConfig fallback — resolve display names ("Table Tennis"),
  // snake_case keys ("table_tennis") and any casing to the config entry.
  const sportDefaults = findSportConfig(match?.sport_name || match?.sportName)?.defaults || {};

  const points_to_win = savedPtw   ?? sportDefaults.points_to_win ?? null;
  const win_by_margin = savedMargin ?? sportDefaults.win_by_margin ?? 1;
  const max_points    = savedMax    ?? sportDefaults.max_points    ?? null;

  return { points_to_win, win_by_margin, max_points };
};

/**
 * Check whether a proposed score increment is allowed / triggers a win.
 *
 * @param {string}  participant   - "participant1" | "participant2"
 * @param {object}  currentScore  - { participant1: n, participant2: n }
 * @param {object}  match
 * @returns {{ type: string, ... } | null}
 *   null          → score is fine, proceed normally
 *   { type: 'max_blocked', message }           → score would exceed max; block it
 *   { type: 'win', winnerParticipant, winnerName, opponentScore, message }  → declare winner
 */
export const validateScoreIncrease = (participant, currentScore, match) => {
  const { points_to_win, win_by_margin, max_points } = getMatchScoringRules(match);

  const myPrev      = currentScore[participant] || 0;
  const myNew       = myPrev + 1;
  const opponent    = participant === "participant1" ? "participant2" : "participant1";
  const opponentScore = currentScore[opponent] || 0;

  // ── 1. Hard cap: score cannot exceed max_points ──────────────────────────
  if (max_points !== null && max_points !== 999 && myNew > max_points) {
    return {
      type: "max_blocked",
      message: `Maximum score of ${max_points} has been reached. You cannot score beyond this limit.`,
    };
  }

  // ── 2. At max_points: score IS at cap, only allow if already permitted ───
  //   (handled above — if myNew === max_points it's still valid)

  // ── 3. Win condition check ────────────────────────────────────────────────
  if (points_to_win !== null) {
    const margin = myNew - opponentScore;
    const meetsMarginRule = win_by_margin <= 1 ? true : margin >= win_by_margin;

    if (myNew >= points_to_win && meetsMarginRule) {
      const winnerName = getParticipantName(match, participant);
      const isAtCap = max_points !== null && max_points !== 999 && myNew >= max_points;
      return {
        type: "win",
        winnerParticipant: participant,
        winnerName,
        newScore: myNew,
        opponentScore,
        isAtCap,
        message: isAtCap
          ? `${winnerName} has reached the maximum score cap (${max_points}). Complete the match?`
          : `${winnerName} has reached ${myNew} point${myNew !== 1 ? "s" : ""} and wins by ${margin}! Complete the match?`,
      };
    }

    // ── 4. Approaching cap without meeting margin (deuce-like) ──────────────
    //   If my new score == max_points and I still don't have the margin → still block
    if (max_points !== null && max_points !== 999 && myNew >= max_points && !meetsMarginRule) {
      const winnerScore = (opponentScore > myNew) ? opponentScore : myNew;
      const loserScore  = (opponentScore > myNew) ? myNew : opponentScore;
      const ahead = getParticipantName(match, opponentScore > myNew ? opponent : participant);
      return {
        type: "cap_no_margin",
        message: `Both teams are at or near the cap (${max_points}). ${ahead} leads ${winnerScore}–${loserScore}. Consider completing the match or playing on without margin.`,
        winnerParticipant: opponentScore > myNew ? opponent : participant,
        winnerName: ahead,
        newScore: myNew,
        opponentScore,
      };
    }
  }

  return null;
};
